import { Document } from "@syadem/kairos-citizen-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useKairosApi } from "./useKairosApi";

export function useHealthRecordDocuments(id: string): {
  isLoading: boolean;
  documents?: Document[];
  error?: Response | Error;
  refreshDocuments: (params?: { hideLoading: boolean }) => void;
} {
  const api = useKairosApi();

  const [documents, setDocuments] = useState<Document[] | undefined>(undefined);
  const [error, setError] = useState<Response | Error | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const fetchingRef = useRef(false);

  // TODO: Remove hideLoading after we start using the store as state
  // (in Documents, every operation needs a refresh because we are not using a local store)
  // (so "hideLoading" is used to silently refresh the documents)
  const fetchDocuments = useCallback(
    async ({ hideLoading }: { hideLoading: boolean } = { hideLoading: false }) => {
      if (!api?.documentApi) return;

      fetchingRef.current = true;
      if (!hideLoading) setIsLoading(true);
      setError(undefined);
      try {
        const { documents } = await api.documentApi.indexDocuments(id);
        setDocuments(documents);
      } catch (error) {
        if (error instanceof Response || error instanceof Error) {
          setError(error);
        } else {
          throw error;
        }
      } finally {
        fetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [api, id],
  );

  useEffect(() => {
    if (fetchingRef.current) return;
    fetchDocuments();
  }, [fetchDocuments]);

  return { isLoading, documents, error, refreshDocuments: fetchDocuments };
}
